import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { DatosParaPagar } from "@/componentes/catalogos/DatosParaPagar"
import { precioLineaValido, precioValido } from "@/componentes/ordenes/lineas"
import type { Orden } from "@/componentes/ordenes/tipos"
import { esCuentaBloqueada, PinEnLinea } from "@/componentes/tesoreria/CuentaBloqueada"
import { aCentimos, comoMonto, monto as monto_ } from "@/componentes/tesoreria/dinero"
import type { Cuenta } from "@/componentes/tesoreria/tipos"
import { useTransferirFondos } from "@/componentes/tesoreria/useTransferirFondos"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

type MetodoPago = { id: number; nombre: string; descripcion: string | null }

type Propuesta = {
  proveedor: string
  divisa_orden: string
  divisa_cuenta: string
  ordenes: { id: number; codigo: string; concepto: string; pendiente: string }[]
  total_pendiente: string
  monto_debitado: string
  tasa_aplicada: string | null
  comision: string
  aplica_igtf: boolean
  monto_igtf: string
  saldo_disponible: string
  permite_sobregiro: boolean
  fondeo_automatico: boolean
  /** Lo que habría que traer del cambio que financió las órdenes. `null` si no hay. */
  comision_a_traer: string | null
  cuenta_comision: string | null
  divisa_comision: string | null
}

function hoy(): string {
  const d = new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/**
 * Pagar JUNTAS varias órdenes del mismo proveedor en una transferencia (19/10).
 *
 * ⚠ **Un movimiento por el total y una ejecución por orden** (`services/pago_combinado.py`):
 * el extracto enseña lo que enseña el banco, con UNA comisión y UN IGTF.
 *
 * ⚠ **Cada orden se paga ENTERA.** En la misma moneda lo debitado es la suma de lo
 * pendiente y no se edita; en otra, se teclea lo que debitó el banco y se reparte en
 * proporción. Pagar otra cifra se hace orden por orden, donde se decide la diferencia.
 */
export function ModalPagoCombinado({
  ordenes,
  onCerrar,
}: {
  ordenes: Orden[]
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [cuentaId, setCuentaId] = useState<number | null>(null)
  const [metodoId, setMetodoId] = useState<number | null>(null)
  const [monto, setMonto] = useState("")
  const [comision, setComision] = useState("0")
  const [aplicaIgtf, setAplicaIgtf] = useState(false)
  const [igtf, setIgtf] = useState("0")
  const [referencia, setReferencia] = useState("")
  const [fecha, setFecha] = useState(hoy())
  const [comprobante, setComprobante] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])
  const [conFondeo, setConFondeo] = useState(true)
  // Por omisión SÍ se trae, como en el pago de una orden: sin ella, un pago corto sólo
  // por la comisión pedía una segunda transferencia de recursos (19/10).
  const [traerComision, setTraerComision] = useState(true)
  const [sinComprobante, setSinComprobante] = useState(false)
  const [motivoSinComprobante, setMotivoSinComprobante] = useState("")
  const montoTocado = useRef(false)
  const usuario = usarSesion((e) => e.usuario)
  const puedeSinComprobante = tienePermiso(usuario, "tesoreria:ejecutar_sin_comprobante")

  const ids = ordenes.map((o) => o.id)
  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
  })
  const cuenta = (cuentas.data ?? []).find((c) => c.id === cuentaId) ?? null
  const metodos = useQuery({
    queryKey: ["metodos-pago", "activos", cuenta?.divisa_id ?? null],
    queryFn: () =>
      api.get<MetodoPago[]>(
        `/api/v1/metodos-pago?activo=true${cuenta ? `&divisa_id=${cuenta.divisa_id}` : ""}`,
      ),
  })
  const propuesta = useQuery({
    queryKey: ["propuesta-pago-combinado", ids, cuentaId, metodoId, fecha],
    queryFn: () =>
      api.get<Propuesta>(
        `/api/v1/pagos-combinados/propuesta?cuenta_id=${cuentaId}${ids
          .map((id) => `&orden_ids=${id}`)
          .join("")}${metodoId !== null ? `&metodo_pago_id=${metodoId}` : ""}${
          fecha ? `&fecha_valor=${fecha}` : ""
        }`,
      ),
    enabled: cuentaId !== null,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (metodoId === null || metodos.data === undefined) return
    if (!metodos.data.some((m) => m.id === metodoId)) setMetodoId(null)
  }, [metodos.data, metodoId])

  useEffect(() => {
    const p = propuesta.data
    if (!p) return
    if (!montoTocado.current) setMonto(p.monto_debitado)
    setComision(p.comision)
    setAplicaIgtf(p.aplica_igtf)
    setIgtf(p.monto_igtf)
  }, [propuesta.data])

  const mismaMoneda =
    propuesta.data !== undefined && propuesta.data.divisa_orden === propuesta.data.divisa_cuenta
  const moneda = propuesta.data?.divisa_cuenta ?? ""

  const pagar = useMutation({
    meta: {
      exito: {
        titulo: "Pago registrado",
        texto: `Se pagaron juntas ${ordenes.map((o) => o.codigo).join(", ")}.`,
      },
    },
    mutationFn: () => {
      const datos = new FormData()
      datos.append(
        "datos",
        JSON.stringify({
          orden_ids: ids,
          cuenta_id: cuentaId,
          metodo_pago_id: metodoId,
          monto_debitado: precioValido(monto),
          comision: comision.trim() === "" ? "0" : precioLineaValido(comision),
          monto_igtf: aplicaIgtf ? precioLineaValido(igtf) : "0",
          referencia: referencia.trim(),
          fecha_valor: fecha,
          motivo_sin_comprobante: sinComprobante ? motivoSinComprobante.trim() : null,
          traer_comision: puedeTraerComision && traerComision,
          ...(propuesta.data?.fondeo_automatico && !conFondeo ? { con_fondeo: false } : {}),
        }),
      )
      if (!sinComprobante && comprobante[0]) datos.append("comprobante", comprobante[0].archivo)
      return api.post<Orden[]>("/api/v1/pagos-combinados", datos)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      clienteQuery.invalidateQueries({ queryKey: ["orden-pago"] })
      clienteQuery.invalidateQueries({ queryKey: ["cuentas-bancarias"] })
      onCerrar()
    },
  })

  const partes = [precioValido(monto), comision.trim() === "" ? "0" : precioLineaValido(comision)]
  if (aplicaIgtf) partes.push(precioLineaValido(igtf))
  const validos = partes.every((p) => p !== null)
  const total = validos ? partes.reduce((s, p) => s + aCentimos(p ?? "0"), 0n) : null
  // ⚠ Lo que se trae del cambio ENTRA antes de debitar y cuenta para el saldo, como en
  // el servidor: comisión + IGTF, en la moneda de la cuenta que paga.
  const puedeTraerComision =
    propuesta.data?.comision_a_traer != null && aCentimos(propuesta.data.comision_a_traer) > 0n
  const entraDelCambio =
    puedeTraerComision && traerComision && validos
      ? aCentimos(comision.trim() === "" ? "0" : (precioLineaValido(comision) ?? "0")) +
        (aplicaIgtf ? aCentimos(precioLineaValido(igtf) ?? "0") : 0n)
      : 0n
  const saldo = propuesta.data ? aCentimos(propuesta.data.saldo_disponible) + entraDelCambio : null
  const corto = total !== null && saldo !== null && total > saldo
  const alcanza =
    total !== null && saldo !== null && (!corto || propuesta.data?.permite_sobregiro === true)

  // Si no alcanza, lo primero es traer el dinero, sin salir del pago (19/10).
  const debitadoC = precioValido(monto) === null ? null : aCentimos(precioValido(monto) ?? "0")
  const faltante =
    corto && propuesta.data?.permite_sobregiro !== true && saldo !== null && total !== null
      ? debitadoC !== null && debitadoC > saldo
        ? debitadoC - saldo
        : total - saldo
      : null
  const fondos = useTransferirFondos({
    cuentas: cuentas.data ?? [],
    cuentaPagaId: cuentaId,
    faltante,
    moneda,
    ordenIds: ids,
  })

  const listo =
    cuentaId !== null &&
    propuesta.data !== undefined &&
    precioValido(monto) !== null &&
    validos &&
    alcanza &&
    referencia.trim().length >= 3 &&
    fecha !== "" &&
    (sinComprobante ? motivoSinComprobante.trim().length >= 10 : comprobante.length === 1)

  const error =
    pagar.error instanceof ErrorApi && !esCuentaBloqueada(pagar.error) ? pagar.error.message : null
  const divisaOrden = ordenes[0]?.divisa ?? ""
  const totalOrdenes = ordenes.reduce(
    (s, o) => s + aCentimos(o.monto_pendiente || o.monto_neto),
    0n,
  )

  return (
    <>
      {fondos.dialogo}
      <Modal
        abierto={!fondos.transfiriendo}
        titulo={`Pagar juntas · ${ordenes.length} órdenes`}
        descripcion={`${ordenes[0]?.proveedor ?? ""} · ${comoMonto(totalOrdenes)} ${divisaOrden} en una sola transferencia`}
        onCerrar={onCerrar}
      >
        <form
          className="space-y-4"
          onSubmit={(evento) => {
            evento.preventDefault()
            if (listo) pagar.mutate()
          }}
          onPaste={(evento) => {
            const archivos = archivosPegados(evento.clipboardData, 0)
            if (archivos.length === 0) return
            evento.preventDefault()
            const siguiente = sumar([], archivos.slice(0, 1))
            setComprobante(siguiente.adjuntos)
            setRechazados(siguiente.rechazados)
          }}
        >
          <ul className="tarjeta divide-y text-sm" style={{ borderColor: "var(--border)" }}>
            {ordenes.map((o) => (
              <li key={o.id} className="flex items-baseline justify-between gap-3 p-3">
                <span className="min-w-0">
                  <span className="tabular-nums font-medium">{o.codigo}</span>{" "}
                  <span style={{ color: "var(--fg-muted)" }}>{o.concepto}</span>
                </span>
                <span className="shrink-0 tabular-nums">
                  {monto_(o.monto_pendiente || o.monto_neto)} {o.divisa ?? ""}
                </span>
              </li>
            ))}
          </ul>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo para="combinado-cuenta" etiqueta="Cuenta que paga">
              <SelectorBuscable
                id="combinado-cuenta"
                opciones={(cuentas.data ?? [])
                  .filter((c) => c.activo)
                  .map((c) => ({
                    valor: c.id,
                    etiqueta: c.nombre,
                    detalle: [c.banco, c.ultimos4 ? `···${c.ultimos4}` : null]
                      .filter(Boolean)
                      .join(" "),
                  }))}
                valor={cuentaId}
                onCambiar={(id) => {
                  montoTocado.current = false
                  setCuentaId(id)
                }}
                placeholder="Buscar cuenta…"
              />
            </Campo>
            <Campo para="combinado-metodo" etiqueta="Método de pago">
              <SelectorBuscable
                id="combinado-metodo"
                opciones={(metodos.data ?? []).map((m) => ({
                  valor: m.id,
                  etiqueta: m.nombre,
                  detalle: m.descripcion ?? undefined,
                }))}
                valor={metodoId}
                onCambiar={setMetodoId}
                vacio="Sin especificar"
              />
            </Campo>
          </div>

          {/* Los datos del proveedor que sirven al método elegido (03/11, pedido del
              usuario): la cuenta para una transferencia, el teléfono para un pago móvil.
              La `key` por método vuelve a «sólo los de este método» al cambiarlo. */}
          {ordenes[0] && (
            <DatosParaPagar
              key={metodoId ?? "sin-metodo"}
              proveedorId={ordenes[0].proveedor_id}
              metodo={{
                id: metodoId,
                nombre: metodos.data?.find((m) => m.id === metodoId)?.nombre,
              }}
            />
          )}

          {/* Con PIN, se abre AQUÍ: sin cambiar de pestaña ni cerrar el pago (19/10). */}
          <PinEnLinea error={propuesta.error} />
          {propuesta.error instanceof ErrorApi && !esCuentaBloqueada(propuesta.error) && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {propuesta.error.message}
            </p>
          )}

          {propuesta.data && (
            <fieldset className="tarjeta space-y-4 p-4">
              <legend className="px-1 text-sm font-medium">Lo que sale del banco</legend>
              {fondos.aviso}
              <p className="text-sm">
                Saldo disponible:{" "}
                <strong className="tabular-nums">{monto_(propuesta.data.saldo_disponible)}</strong>{" "}
                {moneda}
                {mismaMoneda
                  ? ` · se debita la suma de lo pendiente: cada orden se paga entera.`
                  : ` · las órdenes son en ${propuesta.data.divisa_orden}: corrige el monto con lo que debitó el banco; se reparte entre las órdenes en proporción.`}
              </p>
              <div className="grid gap-4 sm:grid-cols-3">
                <Campo para="combinado-monto" etiqueta={`Monto debitado ${moneda}`}>
                  <input
                    id="combinado-monto"
                    value={monto}
                    readOnly={mismaMoneda}
                    onChange={(e) => {
                      setMonto(e.target.value)
                      montoTocado.current = true
                    }}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums read-only:opacity-70"
                  />
                </Campo>
                <Campo para="combinado-comision" etiqueta="Comisión bancaria">
                  <input
                    id="combinado-comision"
                    value={comision}
                    onChange={(e) => setComision(e.target.value)}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums"
                  />
                </Campo>
                <Campo para="combinado-igtf" etiqueta="IGTF">
                  <input
                    id="combinado-igtf"
                    value={aplicaIgtf ? igtf : "0"}
                    onChange={(e) => setIgtf(e.target.value)}
                    disabled={!aplicaIgtf}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums disabled:opacity-60"
                  />
                </Campo>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={aplicaIgtf}
                  onChange={(e) => setAplicaIgtf(e.target.checked)}
                />
                Aplica IGTF (pagos en divisas)
              </label>
              {puedeTraerComision && (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={traerComision}
                    onChange={(e) => setTraerComision(e.target.checked)}
                  />
                  <span>
                    Traer también la comisión desde{" "}
                    <strong>{propuesta.data.cuenta_comision}</strong>:{" "}
                    <strong className="tabular-nums">
                      {monto_(propuesta.data.comision_a_traer ?? "0")}
                    </strong>{" "}
                    {propuesta.data.divisa_comision}
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      A la tasa del cambio que financió estas órdenes: no hace falta otra
                      transferencia.
                    </span>
                  </span>
                </label>
              )}
              {propuesta.data.fondeo_automatico && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={conFondeo}
                    onChange={(e) => setConFondeo(e.target.checked)}
                  />
                  Registrar también el ingreso que fondea este pago
                </label>
              )}
              {total !== null && (
                <p className="text-sm">
                  Sale de la cuenta: <strong className="tabular-nums">{comoMonto(total)}</strong>{" "}
                  {moneda}
                  {entraDelCambio > 0n && (
                    <span style={{ color: "var(--fg-muted)" }}>
                      {" "}
                      · antes entran {comoMonto(entraDelCambio)} {moneda} de{" "}
                      {propuesta.data.cuenta_comision}
                    </span>
                  )}
                  {corto &&
                    saldo !== null &&
                    (propuesta.data.permite_sobregiro ? (
                      <span style={{ color: "var(--warning)" }}>
                        {" "}
                        — la cuenta queda en {comoMonto(saldo - total)} {moneda}; admite sobregiro
                      </span>
                    ) : (
                      <span style={{ color: "var(--destructive)" }}> — no alcanza el saldo</span>
                    ))}
                </p>
              )}
            </fieldset>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo para="combinado-referencia" etiqueta="Referencia bancaria">
              <input
                id="combinado-referencia"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                maxLength={60}
                className="campo mt-1"
              />
            </Campo>
            <Campo para="combinado-fecha" etiqueta="Fecha valor">
              <input
                id="combinado-fecha"
                type="date"
                value={fecha}
                max={hoy()}
                onChange={(e) => setFecha(e.target.value)}
                className="campo mt-1"
              />
            </Campo>
          </div>

          <fieldset>
            <legend className="text-sm font-medium">Comprobante del banco</legend>
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {sinComprobante
                ? "Este pago se registra sin comprobante: hace falta decir por qué."
                : "Obligatorio, uno para todas: queda en cada orden. Arrástralo, pégalo con Ctrl+V o elígelo."}
            </p>
            {puedeSinComprobante && (
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sinComprobante}
                  onChange={(e) => {
                    setSinComprobante(e.target.checked)
                    if (e.target.checked) setComprobante([])
                  }}
                />
                Este pago no tiene comprobante
              </label>
            )}
            {sinComprobante ? (
              <Campo para="combinado-sin-comprobante" etiqueta="Por qué no hay comprobante">
                <input
                  id="combinado-sin-comprobante"
                  value={motivoSinComprobante}
                  onChange={(e) => setMotivoSinComprobante(e.target.value)}
                  maxLength={300}
                  className="campo mt-1"
                />
              </Campo>
            ) : (
              <AdjuntosNuevos
                adjuntos={comprobante}
                rechazados={rechazados}
                etiqueta="Adjuntar comprobante"
                multiple={false}
                onArchivos={(archivos) => {
                  const siguiente = sumar([], archivos.slice(0, 1))
                  setComprobante(siguiente.adjuntos)
                  setRechazados(siguiente.rechazados)
                }}
                onQuitar={() => setComprobante([])}
              />
            )}
          </fieldset>

          <PinEnLinea error={pagar.error} onAbierta={() => pagar.reset()} />
          {error && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Boton onClick={onCerrar}>Cancelar</Boton>
            <Boton type="submit" variante="primario" disabled={!listo || pagar.isPending}>
              {pagar.isPending ? "Pagando…" : `Pagar ${ordenes.length} órdenes`}
            </Boton>
          </div>
        </form>
      </Modal>
    </>
  )
}
